import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { compoundFactor } from '@shared/calculations/funding/projection/sharedPolicyConfig';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import type { StrategyPresentation } from './types';

// Asset Care's monthly cap is per care environment and inflates on the AOB
// rate/cap (not the policy-wide rate the default BenefitAmountCell uses), so
// the cell is overridden. Return the derivation pieces so the cell's tooltip
// can show the same base × (1+r)^n math as the default cell.
function perEnvironmentCapBreakdown(month: MonthlyEntry, terms: ClaimTerms) {
  const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
  const baseDaily =
    dailyByEnvironment[month.context.careEnvironment] ?? terms.nominalDailyBenefitBase;
  const baseMonthly = baseDaily * DAYS_IN_MONTH;
  const rate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const capYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const monthlyCap = baseMonthly * compoundFactor(rate, capYears ?? 0, month.context.policyYear);
  return { baseMonthly, rate, capYears, monthlyCap };
}

export const oaAssetCarePresentation: StrategyPresentation = {
  benefitAmountCell: ({ month, terms }) => {
    const { baseMonthly, rate, capYears, monthlyCap } = perEnvironmentCapBreakdown(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    const capped = cap !== null && intYear > cap;
    const effectiveYear = capped ? cap : intYear;
    const increments = Math.max(effectiveYear - 1, 0);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="leading-tight cursor-help">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Inflation capped at year ${cap}` : undefined}
          compoundLabel="Compound growth"
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month, terms }) =>
    perEnvironmentCapBreakdown(month, terms).monthlyCap,

  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC pool breakdown"
        description="Asset Care pays from Acceleration of Benefits (AOB) first, then Continuation of Benefits (COB). Inflation accrues at policy anniversaries while each pool remains open."
        sections={[
          {
            heading: 'Acceleration of Benefits (AOB)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `AOB drew ${currency(baseDrawdown)}; death benefit reduction capped at minimum guaranteed ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="For Asset Care, only AOB drawdown reduces the death benefit. COB drawdown does not reduce death benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced this month', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    if (eligibleDays === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    // OOP reasons. Their sum is the total out-of-pocket, and
    // cost = paid + oopOverCap + oopIneligibleDays — the waterfall below.
    const oopOverCap = Math.max(eligibleCost - paid, 0);
    const oopIneligibleDays = Math.max(cost - eligibleCost, 0);
    const totalOop = oopOverCap + oopIneligibleDays;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit (subject to cap and indemnity percent) while AOB has balance, then reimburses from COB.'
      : 'Reimburses eligible incurred cost, up to the monthly cap.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">
                Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
              </span>
              <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
            </>
          )}
          <span className="text-gray-300">Policy paid</span>
          <span className="font-semibold tabular-nums">−{currency(paid)}</span>
          <span className="text-gray-300">Out of pocket</span>
          <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
          {oopOverCap > 0 && (
            <>
              <span className="pl-2 text-gray-400">· over cap</span>
              <span className="tabular-nums text-rose-300">{currency(oopOverCap)}</span>
            </>
          )}
          {oopIneligibleDays > 0 && (
            <>
              <span className="pl-2 text-gray-400">{'· waiting period / <2 ADL'}</span>
              <span className="tabular-nums text-rose-300">{currency(oopIneligibleDays)}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">
          Drawn from AOB {currency(drawnBase)}
          {drawnEob > 0 ? ` + COB ${currency(drawnEob)}` : ''}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;

    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  // Asset Care's monthly cap uses the AOB inflation rate.
  benefitInflationRate: terms =>
    terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Asset Care draws from Acceleration of Benefits (AOB) first, then Continuation of Benefits (COB). Inflation can accrue at policy anniversaries while pools remain open."
        formulas={[
          { label: 'Closing pool', expression: '(opening AOB + opening COB) + inflation accrual − drawn this month' },
          { label: 'Draw order', expression: 'AOB first; COB engages once AOB is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          { label: 'Reimbursement mode', expression: 'min(eligible cost, monthly cap, pool remaining)' },
          { label: 'Indemnity mode', expression: 'min(monthly cap × indemnity %, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays regardless of actual cost while AOB has balance."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this care environment and policy year, with the daily equivalent beneath. Asset Care's daily benefit is set per care environment and inflates on AOB."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue daily for environment × days in month × <GrowthFactor rate="AOB inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
        ]}
        notes="AOB inflation capped at the configured cap year if specified."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated AOB + COB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any inflation cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'AOB',
            expression: (
              <>
                AOB at issue × <GrowthFactor rate="AOB inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
          {
            label: 'COB',
            expression: (
              <>
                COB at issue × <GrowthFactor rate="COB inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
          { label: 'Total', expression: 'AOB value + COB value' },
        ]}
        notes="A 0% inflation rider on a pool leaves that pool flat. Once a pool's inflation cap is reached, its value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the AOB + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (AOB depletion + COB depletion)' },
          { label: 'Identity', expression: 'Pool limit − Drawn = Pool remaining (while pool is open)' },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Asset Care depletes the death benefit dollar-for-dollar by AOB drawdown only; COB drawdown does not reduce the death benefit."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (AOB drawdown)' },
          { label: 'Floor', expression: 'max(above, minimum guaranteed amount)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
